import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import DataRunsDetailPanel from 'components_new/organisms/internal/DataRuns/DetailPanel'
import DataRunsFileDeliveryPanel from 'components_new/organisms/internal/DataRuns/FileDeliveryPanel'
import RerunDialog from 'components_new/organisms/internal/DataReruns/RerunDialog'
import DataHealthOverviewTab from 'components_new/organisms/internal/DataHealth/OverviewTab'
import DataHealthRerunsTab from 'components_new/organisms/internal/DataHealth/RerunsTab'
import DataHealthRunsTab from 'components_new/organisms/internal/DataHealth/RunsTab'
import DataHealthValidationTab from 'components_new/organisms/internal/DataHealth/ValidationTab'
import DataHealthTemplate from 'components_new/templates/internal/DataHealth'
import {
  DataHealthTab,
  dataHealthTabPath,
  parseDataHealthTab
} from 'components_new/templates/internal/DataHealth/lib'
import PageLayout from 'components_new/templates/PageLayout'

import { useDataRunsActions } from 'redux/actions/internal/DataRuns'
import { useSimFlawActions } from 'redux/actions/internal/SimFlaws'
import {
  useActiveReruns,
  useRerun,
  useReruns
} from 'redux/hooks/internal/DataReruns'
import { useInternalDataRuns } from 'redux/hooks/internal/DataRuns'
import { useInternalSimFlaws } from 'redux/hooks/internal/SimFlaws'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { FileDelivery, JobSummary } from 'redux/selectors/internal/DataRuns'

/**
 * Poll interval while any job in scope or any rerun is still running. The
 * elapsed time and countdown tick locally; this is only about picking up
 * jobs that started or finished since the last fetch.
 */
const POLL_INTERVAL_RUNNING_MS = 30 * 1000

/** Poll interval otherwise - data comes from MySQL, so this is cheap. */
const POLL_INTERVAL_IDLE_MS = 5 * 60 * 1000

/**
 * Datahälsa - Datavalidering and Jobb under one route.
 *
 * The customer selection lives here rather than in a tab, which is the whole
 * point of the merge: switching tab keeps the customer you were looking at,
 * and the run context in the header is fetched once for all tabs.
 *
 * The brist aggregates are fetched here too, although only Datavalidering
 * shows them: the tab bar carries their total on every tab.
 */
const DataHealth = () => {
  const { tab } = useParams<{ tab?: string }>()
  const history = useHistory()

  const activeTab = parseDataHealthTab(tab)

  const dataRuns = useInternalDataRuns()
  const simFlaws = useInternalSimFlaws()

  const { isInProductTeam } = useAccountSettings()

  const { tryGetDataRuns } = useDataRunsActions()
  const { tryGetFlawSettings, tryGetSimFlawAggregates } = useSimFlawActions()

  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null
  )

  const selectedCustomerName = selectedCustomerId
    ? dataRuns.customerOptions.find(
      (customer) => customer.id === selectedCustomerId
    )?.name ?? null
    : null

  const [rerunDialog, setRerunDialog] = useState<{
    open: boolean
    preselectedCustomerId: string | null
  }>({ open: false, preselectedCustomerId: null })

  const [selectedJob, setSelectedJob] = useState<JobSummary | null>(null)
  const [selectedDelivery, setSelectedDelivery] = useState<FileDelivery | null>(
    null
  )

  /** null = the active rerun, else the latest one. */
  const [selectedRerunId, setSelectedRerunId] = useState<string | null>(null)

  const { activeReruns, refetch: refetchActiveReruns } = useActiveReruns()
  const reruns = useReruns({
    enabled: activeTab === 'overview' || activeTab === 'runs'
  })

  const activeRerun = activeReruns[0] ?? null
  const latestRerun = activeRerun ?? reruns.reruns?.[0] ?? null
  const shownRerunId = selectedRerunId ?? latestRerun?.id ?? null

  const rerun = useRerun(activeTab === 'runs' ? shownRerunId : null)

  const refetchReruns = reruns.refetch
  const refetchRerun = rerun.refetch

  const fetchAll = () => {
    tryGetDataRuns(selectedCustomerId ?? undefined)
    tryGetSimFlawAggregates(selectedCustomerId ?? undefined)
  }

  // stable so the poll below restarts only when what it fetches changes
  const refresh = useCallback(() => {
    tryGetDataRuns(selectedCustomerId ?? undefined)
    tryGetSimFlawAggregates(selectedCustomerId ?? undefined)
    refetchActiveReruns()
    refetchReruns()
    refetchRerun()
  }, [
    refetchActiveReruns,
    refetchRerun,
    refetchReruns,
    selectedCustomerId,
    tryGetDataRuns,
    tryGetSimFlawAggregates
  ])

  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId])

  useEffect(() => {
    if (!simFlaws.settingsFetched) {
      tryGetFlawSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasRunningJob =
    (dataRuns.data?.running ?? []).length > 0 || activeRerun !== null

  useEffect(() => {
    const intervalMs = hasRunningJob
      ? POLL_INTERVAL_RUNNING_MS
      : POLL_INTERVAL_IDLE_MS

    const interval = setInterval(refresh, intervalMs)

    return () => clearInterval(interval)
  }, [hasRunningJob, refresh])

  const handleRerunCreated = () => {
    setSelectedRerunId(null)
    fetchAll()
    refetchActiveReruns()
    refetchReruns()
  }

  const handleRerunCancelled = () => {
    refetchRerun()
    refetchActiveReruns()
    refetchReruns()
  }

  const handleFailedRetried = (rerunId: string) => {
    setSelectedRerunId(rerunId)
    refetchActiveReruns()
    refetchReruns()
  }

  // One panel at a time: they occupy the same edge of the screen, and two
  // stacked drawers would hide whichever opened first.
  const selectJob = (job: JobSummary) => {
    setSelectedDelivery(null)
    setSelectedJob(job)
  }

  const selectDelivery = (delivery: FileDelivery) => {
    setSelectedJob(null)
    setSelectedDelivery(delivery)
  }

  const goToTab = (nextTab: DataHealthTab) => {
    // The drawers belong to the Jobb tab; leaving it with one open
    // would leave it hanging over a tab it says nothing about.
    setSelectedJob(null)
    setSelectedDelivery(null)
    history.push(dataHealthTabPath(nextTab))
  }

  /** The Översikt table hands off to Jobb with a customer selected. */
  const goToCustomerRuns = (customerId: string | null) => {
    setSelectedCustomerId(customerId)
    goToTab('jobs')
  }

  const openRerunDialog = (preselectedCustomerId: string | null) => {
    setRerunDialog({ open: true, preselectedCustomerId })
  }

  return (
    <PageLayout
      content={{ disablePadding: true, fullWidth: true }}
      topbar={{
        breadcrumbs: [{ title: 'Admin' }, { title: 'Datahälsa' }]
      }}
      variant="static"
    >
      <DataHealthTemplate
        activeRerun={activeRerun}
        activeTab={activeTab}
        canRerun={isInProductTeam}
        customerOptions={dataRuns.customerOptions}
        data={dataRuns.data}
        dataFetching={dataRuns.dataFetching}
        flawCount={simFlaws.aggregates?.total_count ?? null}
        onRefresh={refresh}
        onRerun={() => openRerunDialog(null)}
        onSelectCustomer={setSelectedCustomerId}
        onSelectTab={goToTab}
        selectedCustomerId={selectedCustomerId}
      >
        {activeTab === 'overview' ? (
          <DataHealthOverviewTab
            data={dataRuns.data}
            dataFetching={dataRuns.dataFetching}
            onSelectCustomer={goToCustomerRuns}
            onSelectReruns={() => goToTab('runs')}
            rerun={latestRerun}
            selectedCustomerName={selectedCustomerName}
          />
        ) : null}

        {activeTab === 'jobs' ? (
          <DataHealthRunsTab
            customerOptions={dataRuns.customerOptions}
            data={dataRuns.data}
            onSelectFileDelivery={selectDelivery}
            onSelectJobRun={selectJob}
            selectedCustomerId={selectedCustomerId}
          />
        ) : null}

        {activeTab === 'runs' ? (
          <DataHealthRerunsTab
            canRerun={isInProductTeam}
            onFailedRetried={handleFailedRetried}
            onRerunCancelled={handleRerunCancelled}
            onSelectRerun={setSelectedRerunId}
            reruns={reruns.reruns}
            rerunsFetching={reruns.loading}
            selectedRerun={rerun.rerun}
            selectedRerunFetching={rerun.loading}
          />
        ) : null}

        {activeTab === 'validation' ? (
          <DataHealthValidationTab
            customerCount={dataRuns.customerOptions.length}
            selectedCustomerId={selectedCustomerId}
            selectedCustomerName={selectedCustomerName}
          />
        ) : null}
      </DataHealthTemplate>

      <DataRunsDetailPanel
        job={selectedJob}
        jobs={dataRuns.data?.jobs ?? []}
        onClose={() => setSelectedJob(null)}
        onRerunCustomer={isInProductTeam ? openRerunDialog : undefined}
        workflows={dataRuns.data?.workflows ?? []}
      />

      <DataRunsFileDeliveryPanel
        delivery={selectedDelivery}
        onClose={() => setSelectedDelivery(null)}
      />

      {isInProductTeam ? (
        <RerunDialog
          customerId={selectedCustomerId}
          onClose={() => setRerunDialog({ ...rerunDialog, open: false })}
          onCreated={handleRerunCreated}
          open={rerunDialog.open}
          preselectedCustomerId={rerunDialog.preselectedCustomerId}
        />
      ) : null}
    </PageLayout>
  )
}

export default DataHealth
